import {AttrsSchema, Component} from 'panel';
import {VirtualElement} from '@floating-ui/dom';

import * as colors from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {Directions, PositionParentType} from '../drop-menu/types';
import {TooltipBulletStyle, TooltipSection} from './types';
import {template} from './template';
import css from './index.inline.styl';

export type ChartTooltipAttrs = {
  sections: TooltipSection[];
  'footer-text'?: string;
  'title-color'?: string;
  direction?: Directions;
  offset?: number;
  skidding?: number;
};

type MousePositioning = {clientX: number; clientY: number};
type ParentPositioning = {positionParent: HTMLElement | PositionParentType.ParentElement};

// can either position automatically relative to an element or absolute mouse coordinates for "follow" behavior
export type ChartTooltipPositioning = ParentPositioning | MousePositioning;

function isParentPositioning(positioning: ChartTooltipPositioning): positioning is ParentPositioning {
  return (
    `positionParent` in positioning &&
    (positioning.positionParent instanceof HTMLElement ||
      positioning.positionParent === PositionParentType.ParentElement)
  );
}

export type ChartTooltipProps = {
  // When provided, the tooltip will automatically position itself relative to this element using floating-ui.
  // See mp-drop-menu for details.
  positioning?: ChartTooltipPositioning;
};

export interface ChartTooltipJsxProps {
  attrs?: ChartTooltipAttrs;
  props?: ChartTooltipProps;
}

type ChartTooltipState = {
  positioning: ChartTooltipPositioning | null;
};

export class ChartTooltip extends Component<ChartTooltipState, ChartTooltipAttrs> {
  static get attrsSchema(): AttrsSchema<ChartTooltipAttrs> {
    return {
      ...super.attrsSchema,
      'footer-text': {type: `string`},
      sections: {
        type: `json`,
        description: `interface: TooltipSection[]`,
        default: [],
      },
      'title-color': {type: `string`},
      offset: {type: `number`, default: 4},
      skidding: {type: `number`, default: 0, description: `Amount of shifting parallel to parent`},
      direction: {
        description: `Chart tooltip opens on the top of an element if 'up', on the bottom if 'down'`,
        type: `string`,
        default: Directions.Right,
        enum: Object.values(Directions),
      },
    };
  }

  get config() {
    return {
      template,
      useShadowDom: true,
      css,
      defaultState: {
        positioning: null,
      },
    };
  }

  set positioning(positioning: ChartTooltipPositioning) {
    this.update({positioning});
  }

  getPositionParent(): HTMLElement | VirtualElement {
    const {positioning} = this.state;
    if (positioning && isParentPositioning(positioning)) {
      if (positioning.positionParent === PositionParentType.ParentElement) {
        return this[positioning.positionParent];
      } else {
        return positioning.positionParent;
      }
    } else {
      return {
        getBoundingClientRect: () => {
          const {clientX, clientY} = positioning as MousePositioning;
          return new DOMRect(clientX, clientY);
        },
      };
    }
  }

  getItemBackground(item) {
    const {bulletStyle, bulletColor} = item;
    return bulletStyle === TooltipBulletStyle.Solid
      ? {background: bulletColor}
      : {
          'background-image': `repeating-linear-gradient(-45deg, ${bulletColor}, ${bulletColor} 25%, ${colors.gray0} 25%, ${colors.gray0} 50%, ${bulletColor} 50%)`,
          'background-size': `4px 4px`,
        };
  }
}

export const ChartTooltipEl = defineAndMakeJsxFactory<ChartTooltipJsxProps>(`mp-chart-tooltip`, ChartTooltip);
export const ChartTooltipReact = panel2react<ChartTooltipJsxProps>(`mp-chart-tooltip`, ChartTooltip);
